// PostMediaGrid.jsx
import React from "react";
import PostImage from "../media/PostImage";
import "./PostMediaGrid.css";

/**
 * Twitter-like media grid for images.
 * - 1 image: natural (keeps your current single-image look)
 * - 2 images: 1x2 (each half the card)
 * - 3 images: featured image full height on the left, two stacked on the right
 * - 4+ images: 2x2 (+N overlay on the 4th)
 *
 * images: [{ url, width?, height? }, ...]
 *
 * NOTE: This file only replaces the raw <img> used in the grid with the
 * PostImage component so preloaded images can show instantly using the
 * PostImage shim/skeleton. No CSS changes.
 */
export default function PostMediaGrid({ images = [], onOpen }) {
  const count = images.length;
  if (count === 0) return null;

  // 1) Single image: keep your natural component
  if (count === 1) {
    const m = images[0];
    return (
      <div className="bf-single-media">
        <PostImage
          src={m.url}
          alt="image 1"
          className="bf-single-media-img"
          loading="lazy"
          width={m.width}
          height={m.height}
          onClick={() => onOpen?.(0)}
        />
      </div>
    );
  }

  // 2) Choose a "featured" image for the 3-up layout.
  // Prefer the largest by area when dims exist, else the first.
  let ordered = images.slice();
  if (count === 3) {
    let featuredIdx = 0;
    let bestScore = -1;
    images.forEach((im, i) => {
      const w = Number(im.width) || 0;
      const h = Number(im.height) || 0;
      const score = w > 0 && h > 0 ? w * h : (i === 0 ? Number.MAX_SAFE_INTEGER / 2 : 0);
      if (score > bestScore) { bestScore = score; featuredIdx = i; }
    });
    if (featuredIdx !== 0) {
      const featured = images[featuredIdx];
      ordered = [featured, ...images.filter((_, i) => i !== featuredIdx)];
    }
  }

  const layoutClass = count === 2 ? "two" : count === 3 ? "three" : "four";
  const toShow = ordered.slice(0, 4); // just in case (you said you'll block 5+ in create)

  return (
    <div className={`bf-media-grid ${layoutClass}`}>
      {toShow.map((m, idx) => {
        const overflow = idx === 3 && count > 4 ? count - 4 : 0;
        return (
          <button
            key={idx}
            type="button"
            className={`bf-cell cell-${idx + 1}`}
            onClick={() => onOpen?.(idx)}
            aria-label={`Open image ${idx + 1}`}
          >
            {/* Use PostImage so shim + skeleton are used.
                We keep inline styles so the image fills the cell and we don't touch CSS. */}
            <div style={{ position: 'relative', width: '100%', height: '100%' }}>
              <PostImage
                src={m.url}
                alt={`image ${idx + 1}`}
                // keep your grid sizing classes if needed (won't affect layout)
                className="bf-cell-img"
                loading="lazy"
                width={m.width}
                height={m.height}
                // ensure PostImage fills the cell visually
                style={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  borderRadius: 0, // grid expects no inner rounding
                }}
                // clicking the image should act like clicking the cell
                onClick={(e) => { e.stopPropagation(); onOpen?.(idx); }}
              />
              {overflow > 0 && <div className="bf-more-overlay">+{overflow}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}
